import { Component, Inject, Input, OnInit, OnDestroy } from '@angular/core';
import { Temporal } from '@js-temporal/polyfill';
import { ProductsFacade, PurchaseFacade } from '@ridango/components';
import { AppStartupService, BaseComponent, Product, ValidityTypes, ValidityType } from '@ridango/core';
import moment from 'moment-timezone';
import { takeUntil } from 'rxjs/operators';
import Env from '@ridango/core/env';
import { TicketValidityFacade } from '../../../../../../services/ticket-validity-facade.service';

@Component({
    selector: 'rwidget-validity',
    templateUrl: './validity.component.html',
    styleUrls: ['./validity.component.scss']
})
export class ValidityComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() isEmpty = false;
    @Input() step = 1;
    locale = 'en';

    selectedProduct$ = this.productsFacade.selected$;
    product: Product;
    isJointTicket = false;

    startDate: Date;
    endDate: Date;
    minDate: Date;
    maxDate: Date;

    jointTicketStartDate: Date;
    jointTicketEndDate: Date;
    jointTicketMinDate = new Date();
    jointTicketMaxDate = new Date();
    jointTicketMaxEndDate: Date;

    validityTypeEnum = ValidityType;
    validityType = ValidityType.activatedOnFirstValidation;

    jointTicketRangeTotalDays$ = this.validityFacade.jointTicketRangeTotalDays$;

    constructor(
        public appConfig: AppStartupService,
        @Inject('ENV') public env: Env,
        private productsFacade: ProductsFacade,
        private purchaseFacade: PurchaseFacade,
        private validityFacade: TicketValidityFacade
    ) {
        super();
    }

    ngOnInit() {
        this.selectedProduct$.pipe(takeUntil(this.componentDestroyed$)).subscribe((product) => {
            if (product) {
                this.validityType = ValidityTypes.get(product);
                this.product = product;
                this.isJointTicket = this.checkIfJointTicket(product);

                if (this.isJointTicket) {
                    this.initializeCustomDateRange();
                } else {
                    switch (this.validityType) {
                        case ValidityType.productHasStartDate:
                            this.getProductDates(product);
                            break;

                        case ValidityType.userSelectedStartDate:
                        case ValidityType.activatedAtPurchase:
                            this.calculateDates(product);
                            break;

                        case ValidityType.activatedOnFirstValidation:
                            this.startDate = null;
                            this.endDate = null;
                            this.resetValidityDates();
                            break;
                    }
                }
            }
        });
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.validityFacade.reset();
    }

    calculateDates(product: Product) {
        const maxDays = this.appConfig.getPropertyByCode('MAX_TICKET_STARTING_DATE_DISTANCE');
        const today = new Date();
        this.startDate = new Date();
        this.endDate = new Date();

        if (product?.product_length > 0 && product.product_type === 'D') {
            this.endDate = this.addDiffToDate(today, product.product_length);
        }

        this.minDate = new Date();
        this.maxDate = this.addDiffToDate(new Date(), +maxDays.value);

        this.setValidityDates();
    }

    getProductDates(product: Product) {
        const tz = this.env.TIMEZONE;
        const getProp = (code: string) => product.product_properties.find((pp) => pp.code === code);

        const fixedStartAndEndProp = getProp('FIXED_START_AND_END_DATE');
        const startDateProp = getProp('TICKET_START_DATE');
        const endDateProp = getProp('TICKET_END_DATE');

        if (!startDateProp || !endDateProp) {
            return;
        }

        const currentMoment = moment.tz(tz);
        const currentYear = currentMoment.year();
        let startYear = currentYear;
        let endYear = currentYear;

        if (fixedStartAndEndProp) {
            const [startMonth, startDay] = startDateProp.value.split('-').map(Number);
            const [endMonth, endDay] = endDateProp.value.split('-').map(Number);
            const currentMonth = currentMoment.month() + 1;
            const currentDay = currentMoment.date();

            // Handle cross-year validity periods (e.g., winter seasons from Nov to Mar)
            if (startMonth > endMonth) {
                const beforeStart = currentMonth < startMonth || (currentMonth === startMonth && currentDay < startDay);
                const afterEnd = currentMonth > endMonth || (currentMonth === endMonth && currentDay > endDay);

                [startYear, endYear] = beforeStart
                    ? [currentYear - 1, currentYear]
                    : afterEnd
                      ? [currentYear, currentYear + 1]
                      : [currentYear, currentYear + 1];
            } else {
                // Same-year period: determine if we're in current or next period
                [startYear, endYear] =
                    endMonth >= currentMonth ? [currentYear - 1, currentYear] : [currentYear, currentYear + 1];
            }
        }

        this.startDate = moment.tz(`${startYear}-${startDateProp.value}`, tz).toDate();
        this.endDate = moment.tz(`${endYear}-${endDateProp.value}`, tz).toDate();

        this.setValidityDates();
    }

    startSelected(date) {
        this.startDate = date;
        this.endDate = null;

        if (this.product?.product_length > 0 && this.product.product_type === 'D') {
            this.endDate = this.addDiffToDate(this.startDate, this.product.product_length);
        }

        this.setValidityDates();
    }

    setRightDateTimezone(date: Date, offset = new Temporal.Duration()) {
        return moment(date)
            .tz(this.appConfig.getTimezone(), true)
            .startOf('day')
            .add({ minutes: offset.total('minutes') })
            .format();
    }

    addDiffToDate(date: Date, diff: number): Date {
        return this.appConfig.calculateTicketEndDate(date, diff);
    }

    setValidityDates() {
        if (!this.startDate) {
            return;
        }

        const startDateTimezone = this.setRightDateTimezone(this.startDate);
        let endDateTimezone;

        // Set ending date for all validity types that have an endDate calculated
        // The endDate is already calculated by calculateTicketEndDate with proper offset handling
        if (this.endDate) {
            const shouldApplyOffset = this.appConfig.isTransportationDayOffsetEnabled();

            let endMoment = moment(this.endDate).tz(this.appConfig.getTimezone(), true).endOf('day');

            if (shouldApplyOffset) {
                const endDateInstant = Temporal.Instant.fromEpochMilliseconds(this.endDate.getTime());
                const offsetWithCorrectTime = this.appConfig.getTransportationDayOffset(endDateInstant);
                endMoment = endMoment.add({ minutes: offsetWithCorrectTime.total('minutes') });
            }

            endDateTimezone = endMoment.format();
        }

        this.purchaseFacade.setTicketValidityDates(startDateTimezone, endDateTimezone);
    }

    resetValidityDates() {
        this.purchaseFacade.unsetTicketValidityDates();
    }

    initializeCustomDateRange() {
        const maxPeriodDays = this.env.JOINT_TICKET_MAX_PERIOD;

        this.jointTicketMinDate = new Date();
        this.jointTicketMinDate.setHours(0, 0, 0, 0);

        this.jointTicketStartDate = new Date(this.jointTicketMinDate);
        this.jointTicketStartDate.setHours(0, 0, 0, 0);

        this.jointTicketEndDate = new Date(this.jointTicketMinDate);
        this.jointTicketEndDate.setHours(23, 59, 59, 999);

        this.jointTicketMaxDate = new Date(this.jointTicketMinDate);
        this.jointTicketMaxDate.setDate(this.jointTicketMinDate.getDate() + maxPeriodDays - 1);

        this.updateMaxEndDate();
        this.calculatePeriodDays();
        this.setCustomValidityDates();
    }

    setCustomValidityDates() {
        if (this.jointTicketStartDate && this.jointTicketEndDate) {
            const startDateTimezone = this.setRightDateTimezone(this.jointTicketStartDate);

            const endDateInstant = Temporal.Instant.fromEpochMilliseconds(this.jointTicketEndDate.getTime());
            const offset = this.appConfig.getTransportationDayOffset(endDateInstant);

            const endDateTimezone = moment(this.jointTicketEndDate)
                .tz(this.appConfig.getTimezone(), true)
                .endOf('day')
                .add({ minutes: offset.total('minutes') })
                .format();

            this.purchaseFacade.setTicketValidityDates(startDateTimezone, endDateTimezone);
        }
    }

    public onCustomStartDateChange(date: Date): void {
        this.jointTicketStartDate = new Date(date);
        this.jointTicketStartDate.setHours(0, 0, 0, 0);

        this.updateMaxEndDate();

        if (this.jointTicketEndDate < this.jointTicketStartDate) {
            this.jointTicketEndDate = new Date(this.jointTicketStartDate);
            this.jointTicketEndDate.setHours(23, 59, 59, 999);
        }

        this.calculatePeriodDays();
        this.setCustomValidityDates();
    }

    public onCustomEndDateChange(date: Date): void {
        this.jointTicketEndDate = new Date(date);
        this.jointTicketEndDate.setHours(23, 59, 59, 999);
        this.updateStartBoundsFromEnd();
        this.clampStartDateToBounds();
        this.calculatePeriodDays();
        this.setCustomValidityDates();
    }

    private updateStartBoundsFromEnd(): void {
        const maxPeriodDays = this.env.JOINT_TICKET_MAX_PERIOD;
        const todayFloor = new Date();
        todayFloor.setHours(0, 0, 0, 0);

        const newMax = new Date(this.jointTicketEndDate);
        newMax.setHours(0, 0, 0, 0);
        this.jointTicketMaxDate = newMax;

        const earliestStart = new Date(this.jointTicketEndDate);
        earliestStart.setDate(earliestStart.getDate() - maxPeriodDays + 1);
        earliestStart.setHours(0, 0, 0, 0);
        this.jointTicketMinDate = earliestStart.getTime() < todayFloor.getTime() ? todayFloor : earliestStart;
    }

    private clampStartDateToBounds(): void {
        if (!this.jointTicketStartDate) {
            return;
        }

        if (this.jointTicketMinDate && this.jointTicketStartDate < this.jointTicketMinDate) {
            this.jointTicketStartDate = new Date(this.jointTicketMinDate);
            this.jointTicketStartDate.setHours(0, 0, 0, 0);
        }

        if (this.jointTicketMaxDate && this.jointTicketStartDate > this.jointTicketMaxDate) {
            this.jointTicketStartDate = new Date(this.jointTicketMaxDate);
            this.jointTicketStartDate.setHours(0, 0, 0, 0);
        }
    }

    updateMaxEndDate() {
        if (this.jointTicketStartDate) {
            const maxPeriodDays = this.env.JOINT_TICKET_MAX_PERIOD;
            this.jointTicketMaxEndDate = new Date(this.jointTicketStartDate);
            this.jointTicketMaxEndDate.setDate(this.jointTicketStartDate.getDate() + maxPeriodDays - 1);
        }
    }

    calculatePeriodDays() {
        if (this.jointTicketStartDate && this.jointTicketEndDate) {
            const timeDiff = this.jointTicketEndDate.getTime() - this.jointTicketStartDate.getTime();
            this.validityFacade.updateJointTicketRangeTotalDays(timeDiff);
        }
    }

    private checkIfJointTicket(product: Product): boolean {
        return product.product_properties?.some((prop) => prop.code === 'DYNAMIC_LENGTH') ?? false;
    }
}
